import { observer } from 'mobx-react-lite'
import useTable from '@ui-components-3/ui/lib/components/Table/hooks/useTable'
import CommonTable from 'components/common/tables/CommonTable/CommonTable'
import Allergy from 'types/PrivateData/Allergy'
import { allergyTypeLabels } from 'constants/memberAllergies'
import { useMemo, useState } from 'react'
import { SortingState } from '@ui-components-3/ui/lib/components/Table/types'
import orderBy from 'lodash/orderBy'
import { TableRowActionsMenu } from '../tables/common'

type ActionItem = {
  label: string
  className?: string
  onClick: (allergy: Allergy) => void
}

type AllergiesTableProps = {
  allergies?: Allergy[]
  sortable?: boolean
  actions?: ActionItem[]
}

const AllergiesTable = ({ allergies, sortable, actions }: AllergiesTableProps) => {
  const [sorting, setSorting] = useState<SortingState>()

  const columns = useMemo(() => {
    // TODO - improve columns type
    const baseColumns: any[] = [
      {
        id: 'allergyDescription',
        header: 'Allergy',
        accessorKey: 'allergyDescription',
        meta: {
          cellClassName: 'xl:w-[250px]',
        },
      },
      {
        id: 'reaction',
        header: 'Reaction',
        accessorKey: 'reaction',
      },
      {
        id: 'allergyType',
        header: 'Type',
        accessorKey: 'allergyType',
        accessorFn: ({ allergyType }: Allergy) => allergyTypeLabels[allergyType],
        enableSorting: !!sortable,
        meta: {
          cellClassName: 'lg:w-[150px]',
        },
      },
    ]

    if (actions?.length) {
      baseColumns.push({
        id: 'actions',
        header: '',
        cell: (cell: any) => (
          <TableRowActionsMenu aria-label="Allergy actions" rowData={cell?.row?.original} actions={actions} />
        ),
        meta: {
          cellClassName: 'w-[50px]',
        },
      })
    }

    return baseColumns
  }, [actions, sortable])

  const data =
    !sorting?.length || !sortable
      ? allergies
      : orderBy(
          allergies,
          sorting.map(({ id }) => id),
          sorting.map(({ desc }) => (desc ? 'desc' : 'asc')),
        )

  const table = useTable({
    // using the model with paginationType 'server' without pagination controls and event handlers
    // makes table acting like static table without pagination at all, so we can use all the benefits from
    // useTable hook as well as reuse existing CommonTable component
    paginationType: 'server',
    columns,
    data,
    rowId: 'allergyDescription',
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  if (!data?.length) {
    return null
  }

  return (
    <div className="max-h-[500px] overflow-y-auto">
      <CommonTable
        aria-label="Allergies Table"
        model={table}
        className="w-full typography-body font-medium"
        containerClassName="!min-h-max !basis-auto"
        hideFooter
      />
    </div>
  )
}

export default observer(AllergiesTable)
